import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Shield, Sparkles, MapPin, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const RAFHA_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/rogqq55c_F1371478-432E-404F-80CC-D2C1450E0839.png";
const CMC_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/33twacgu_IMG_0221.png";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const categories = [
  { id: "dj", name: "DJs", image: "https://images.unsplash.com/photo-1765986964925-4aec53343a8b?w=600&h=800&fit=crop" },
  { id: "photographer", name: "Photographers", image: "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=600&h=800&fit=crop" },
  { id: "musician", name: "Musicians", image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=800&fit=crop" },
  { id: "caterer", name: "Caterers", image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=800&fit=crop" },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Noise Overlay */}
      <div className="noise-overlay"></div>
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Clean Dark Background */}
        <div className="absolute inset-0 bg-[#050505]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08)_0%,transparent_60%)]"></div>
        </div>
        
        {/* Gold Accent Glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.15)_0%,transparent_70%)] pointer-events-none"></div>
        
        <div className="relative z-10 container mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl mx-auto"
          >
            <motion.p 
              className="caption mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Premium Entertainment Services
            </motion.p>
            
            <motion.h1 
              className="font-heading text-5xl sm:text-7xl md:text-8xl font-bold text-white mb-8 tracking-tighter leading-[0.9]"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Elevate Your
              <br />
              <span className="gradient-text-gold">Celebration</span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-12 font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Curated entertainment professionals and unforgettable celebration experiences 
              for life's most memorable moments.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link to="/vendors">
                <Button 
                  data-testid="browse-vendors-btn"
                  className="btn-luxury bg-gold text-black font-bold px-10 py-6 rounded-full text-lg gold-glow"
                >
                  Discover Vendors
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/choice-mobile">
                <Button 
                  data-testid="choice-mobile-btn"
                  variant="outline"
                  className="border-white/20 text-white px-10 py-6 rounded-full text-lg hover:border-gold hover:text-gold transition-all duration-300"
                >
                  Mobile Celebrations
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-gold to-transparent"></div>
        </motion.div>
      </section>
      
      {/* Two Experiences Section */}
      <section className="py-32 md:py-40 px-6 md:px-12 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.05)_0%,transparent_50%)]"></div>
        
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div 
            className="text-center mb-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="caption mb-4">Two Ways to Celebrate</p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight">
              Choose Your Experience
            </h2>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Rafha Entertainment */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Link to="/vendors" className="group block">
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl mb-8">
                  <img 
                    src="https://images.unsplash.com/photo-1596257828689-c822bad48989?w=800&h=1000&fit=crop"
                    alt="Rafha Entertainment"
                    className="w-full h-full object-cover img-luxury transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
                  <div className="absolute inset-0 border border-white/10 rounded-2xl group-hover:border-gold/30 transition-colors duration-500"></div>
                  
                  {/* Logo Badge */}
                  <div className="absolute top-6 left-6 bg-[#050505]/80 backdrop-blur-xl px-5 py-3 rounded-full border border-gold/20 shadow-[0_0_20px_rgba(212,175,55,0.15)]">
                    <img src={RAFHA_LOGO} alt="Rafha" className="h-10 w-auto drop-shadow-[0_0_8px_rgba(212,175,55,0.3)]" />
                  </div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <p className="caption mb-2">Vendor Marketplace</p>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                      Rafha Entertainment
                    </h3>
                    <p className="text-white/60 mb-6 font-light max-w-md">
                      Book vetted DJs, photographers, musicians, and more for your events. 
                      Professional talent, seamless booking.
                    </p>
                    <span className="inline-flex items-center text-gold font-medium group-hover:translate-x-2 transition-transform duration-300">
                      Browse Vendors <ArrowRight className="ml-2 w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
            
            {/* Choice Mobile */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Link to="/choice-mobile" className="group block">
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_d1bbb4e0-9a95-44c0-96ba-d2f3b277900c/artifacts/chpbjqs1_IMG_2052.jpeg"
                    alt="Choice Mobile Van Interior"
                    className="w-full h-full object-cover img-luxury transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
                  <div className="absolute inset-0 border border-white/10 rounded-2xl group-hover:border-gold/30 transition-colors duration-500"></div>
                  
                  {/* Logo Badge */}
                  <div className="absolute top-6 left-6 bg-[#050505]/80 backdrop-blur-xl px-5 py-3 rounded-full border border-gold/20 shadow-[0_0_20px_rgba(212,175,55,0.15)]">
                    <img src={CMC_LOGO} alt="Choice Mobile" className="h-10 w-auto drop-shadow-[0_0_8px_rgba(212,175,55,0.3)]" />
                  </div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <p className="caption mb-2">Mobile Experience</p>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                      Choice Mobile Celebrations
                    </h3>
                    <p className="text-white/60 mb-6 font-light max-w-md">
                      We come to you. Birthday pull-ups, serenades, and milestone celebrations 
                      delivered to your doorstep.
                    </p>
                    <span className="inline-flex items-center text-gold font-medium group-hover:translate-x-2 transition-transform duration-300">
                      Book Experience <ArrowRight className="ml-2 w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Vendor Categories */}
      <section className="py-32 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto">
          <motion.div 
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div>
              <p className="caption mb-4">Rafha Entertainment</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                Featured Categories
              </h2>
            </div>
            <Link to="/vendors">
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:border-gold hover:text-gold rounded-full px-8 py-4"
                data-testid="view-all-categories-btn"
              >
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                variants={fadeInUp}
              >
                <Link 
                  to={`/vendors?category=${category.id}`}
                  data-testid={`category-${category.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                    <img 
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover img-luxury transition-all duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent"></div>
                    <div className="absolute inset-0 border border-white/5 rounded-xl group-hover:border-gold/30 transition-colors duration-500"></div>
                    
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">
                        {category.name}
                      </h3>
                      <span className="text-gold/80 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Explore →
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Choice Mobile Highlight */}
      <section className="py-32 md:py-40 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1920&h=1080&fit=crop"
            alt="Party celebration"
            className="w-full h-full object-cover img-luxury opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]"></div>
        </div>
        
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img src={CMC_LOGO} alt="Choice Mobile" className="h-16 w-auto mb-8" />
              
              <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight mb-6 leading-[1.1]">
                Celebrate Life —<br />
                <span className="text-gold">Right Where You Are</span>
              </h2>
              
              <p className="text-lg text-white/50 mb-10 font-light max-w-lg">
                Our luxury mobile van brings unforgettable celebration experiences directly 
                to homes, churches, and workplaces in Montgomery, AL.
              </p>
              
              <div className="grid grid-cols-3 gap-6 mb-10">
                {[
                  { price: "$175", label: "Celebration Pull-Up", sale: true },
                  { price: "$299", label: "Serenade", sale: true },
                  { price: "$450", label: "VIP Milestone", sale: true },
                ].map((pkg) => (
                  <div key={pkg.label} className="text-center">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-gold">{pkg.price}</p>
                    <p className="text-white/40 text-sm mt-1">{pkg.label}</p>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-6 text-white/50 text-sm mb-10">
                <span className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gold" />
                  Montgomery, AL
                </span>
                <span className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gold" />
                  (877) 295-5905
                </span>
              </div>
              
              <Link to="/choice-mobile">
                <Button 
                  data-testid="book-choice-mobile-btn"
                  className="btn-luxury bg-gold text-black font-bold px-10 py-6 rounded-full text-lg gold-glow"
                >
                  Book Your Experience
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d1bbb4e0-9a95-44c0-96ba-d2f3b277900c/artifacts/ci6tov60_IMG_0205.jpeg"
                  alt="Choice Mobile Celebration Van"
                  className="w-full h-full object-cover img-luxury"
                />
                <div className="absolute inset-0 border border-gold/20 rounded-2xl"></div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 glass-gold px-6 py-4 rounded-xl">
                <p className="text-gold font-heading text-lg font-bold">$150</p>
                <p className="text-white/60 text-sm">Deposit secures booking</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Trust Section */}
      <section className="py-32 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div 
            className="text-center mb-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="caption mb-4">Why Choose Us</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
              The Rafha Difference
            </h2>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                icon: Shield,
                title: "Vetted Professionals",
                description: "Every vendor is carefully screened, licensed, and insured for your peace of mind."
              },
              {
                icon: Star,
                title: "Curated Excellence",
                description: "We partner only with the top-tier talent who deliver exceptional experiences."
              },
              {
                icon: Sparkles,
                title: "Seamless Booking",
                description: "From discovery to payment, our platform makes hiring entertainment effortless."
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                className="card-luxury p-8 md:p-10 rounded-2xl bg-[#0A0A0A]"
              >
                <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">
                  {item.title}
                </h3>
                <p className="text-white/50 font-light leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-32 md:py-40 px-6 md:px-12 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.1)_0%,transparent_60%)]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="caption mb-6">Start Planning</p>
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-8 leading-[1.1]">
              Ready to Create
              <br />
              <span className="gradient-text-gold">Something Unforgettable?</span>
            </h2>
            <p className="text-lg md:text-xl text-white/50 mb-12 font-light max-w-2xl mx-auto">
              Whether you need world-class vendors for your event or a surprise celebration delivered to your door, 
              we're here to make it extraordinary.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/vendors">
                <Button 
                  data-testid="get-started-btn"
                  className="btn-luxury bg-gold text-black font-bold px-12 py-6 rounded-full text-lg gold-glow"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Become a Vendor */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-gradient-to-br from-gold/10 to-transparent rounded-3xl border border-gold/20 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <p className="caption mb-2">For Event Professionals</p>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
                  Join Our Network of Elite Vendors
                </h3>
                <p className="text-white/60 max-w-lg">
                  Get discovered by premium clients, manage bookings effortlessly, and grow your business. 
                  Free to join — only pay when you book work.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/become-vendor">
                  <Button 
                    data-testid="learn-more-vendor-btn"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 rounded-full px-6 py-4 font-medium transition-all duration-300"
                  >
                    Learn More
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button 
                    data-testid="become-vendor-btn"
                    className="bg-gold text-black hover:bg-gold-light rounded-full px-6 py-4 font-bold transition-all duration-300"
                  >
                    Apply Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;
